import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';

function GestionTiposDocumento() {
  const server = process.env.REACT_APP_API_ROUTE;
  const { user } = useUser();
  const { tienePermiso } = useAccesos();

  const [loading, setLoading] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    idTipoDocumento: null,
    descripcion: '',
    abreviatura: '',
    activo: true,
  });

  useEffect(() => {
    cargarTipos();
  }, []);

  const cargarTipos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${server}/ctb/tipo-documentos/all`);
      
      if (!response.ok) {
        throw new Error('Error al cargar datos');
      }
      
      const data = await response.json();
      setTipos(data.$values || data || []);
    } catch (error) {
      console.error('Error al cargar tipos de documento:', error);
      Swal.fire('Error', 'No se pudieron cargar los tipos de documento.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      idTipoDocumento: null,
      descripcion: '',
      abreviatura: '',
      activo: true,
    });
    setEditMode(false);
  };

  const handleOpenModal = (tipo = null) => {
    if (tipo) {
      setForm({
        idTipoDocumento: tipo.idTipoDocumento,
        descripcion: tipo.descripcion || tipo.Descripcion || '',
        abreviatura: tipo.abreviatura || tipo.Abreviatura || '',
        activo: tipo.activo ?? true,
      });
      setEditMode(true);
    } else {
      resetForm();
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetForm();
  };

  const handleSubmit = async () => {
    // Validaciones
    if (!form.descripcion.trim()) {
      Swal.fire('Validación', 'La descripción es obligatoria.', 'warning');
      return;
    }

    const payload = {
      descripcion: form.descripcion.trim(),
      abreviatura: form.abreviatura.trim() || null,
      activo: form.activo,
      ingresadoPor: user?.usuario || 'SYSTEM',
    };

    try {
      setLoading(true);
      const url = editMode
        ? `${server}/ctb/tipo-documentos/${form.idTipoDocumento}`
        : `${server}/ctb/tipo-documentos`;
      
      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.mensaje || `Error HTTP ${response.status}`);
      }

      Swal.fire('Éxito', `Tipo de documento ${editMode ? 'actualizado' : 'creado'} correctamente.`, 'success');
      handleCloseModal();
      await cargarTipos();
    } catch (error) {
      console.error('Error al guardar tipo de documento:', error);
      Swal.fire('Error', error.message || 'No se pudo guardar el tipo de documento.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActivo = async (tipo) => {
    try {
      const response = await fetch(
        `${server}/ctb/tipo-documentos/${tipo.idTipoDocumento}/toggle-active`,
        { method: 'PATCH' }
      );

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      Swal.fire('Éxito', 'Estado actualizado correctamente.', 'success');
      await cargarTipos();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      Swal.fire('Error', 'No se pudo cambiar el estado.', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        const usuarioFormateado = (user?.usuario || 'SYSTEM').padEnd(20, ' ').substring(0, 20);
        const response = await fetch(
          `${server}/ctb/tipo-documentos/${id}/${encodeURIComponent(usuarioFormateado)}`,
          { method: 'DELETE' }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.mensaje || `Error HTTP ${response.status}`);
        }

        Swal.fire('Eliminado', 'Tipo de documento eliminado correctamente.', 'success');
        await cargarTipos();
      } catch (error) {
        console.error('Error al eliminar:', error);
        Swal.fire('Error', error.message || 'No se pudo eliminar el tipo de documento.', 'error');
      }
    }
  };

  return (
    <div className="right_col" role="main">
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>📄 Gestión de Tipos de Documento</h3>
          </div>
        </div>
        <div className="clearfix"></div>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    <DocumentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Tipos de Documento Registrados
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={cargarTipos}
                      disabled={loading}
                      sx={{ mr: 1 }}
                    >
                      Recargar
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenModal()}
                      disabled={loading || !tienePermiso('Editar Tipo Documento')}
                    >
                      Nuevo Tipo
                    </Button>
                  </Box>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>ID</TableCell>
                        <TableCell>Descripción</TableCell>
                        <TableCell>Abreviatura</TableCell>
                        <TableCell align="center">Estado</TableCell>
                        <TableCell>Fecha Ingreso</TableCell>
                        <TableCell>Ingresado Por</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                            Cargando...
                          </TableCell>
                        </TableRow>
                      ) : tipos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                            No hay tipos de documento registrados
                          </TableCell>
                        </TableRow>
                      ) : (
                        tipos.map((tipo) => (
                          <TableRow key={tipo.idTipoDocumento} hover>
                            <TableCell>
                              <Chip label={tipo.idTipoDocumento} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {tipo.descripcion || tipo.Descripcion || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {tipo.abreviatura || tipo.Abreviatura ? (
                                <Chip 
                                  label={tipo.abreviatura || tipo.Abreviatura} 
                                  size="small" 
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={tipo.activo ? 'Desactivar' : 'Activar'}>
                                <Switch
                                  checked={tipo.activo ?? false}
                                  onChange={() => handleToggleActivo(tipo)}
                                  size="small"
                                  color="success"
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {tipo.fechaIngreso 
                                  ? new Date(tipo.fechaIngreso).toLocaleDateString() 
                                  : '-'
                                }
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {tipo.ingresadoPor || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenModal(tipo)}
                                  disabled={!tienePermiso('Editar Tipo Documento')}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(tipo.idTipoDocumento)}
                                  disabled={!tienePermiso('Eliminar Tipo Documento')}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Modal de Formulario */}
        <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editMode ? '✏️ Editar Tipo de Documento' : '➕ Nuevo Tipo de Documento'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descripción *"
                  value={form.descripcion}
                  onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                  placeholder="Ej: Factura, Recibo, Nota de Crédito"
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Abreviatura (Opcional)"
                  value={form.abreviatura}
                  onChange={(e) => setForm(prev => ({ ...prev, abreviatura: e.target.value.toUpperCase() }))}
                  placeholder="Ej: FAC, REC, NC"
                  disabled={loading}
                  inputProps={{ maxLength: 10 }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.activo}
                      onChange={(e) => setForm(prev => ({ ...prev, activo: e.target.checked }))}
                      disabled={loading}
                    />
                  }
                  label="Tipo de documento activo"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} disabled={loading}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              disabled={loading || !tienePermiso('Editar Tipo Documento')}
            >
              {loading ? 'Guardando...' : (editMode ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}

export default GestionTiposDocumento;
